import Cookies from "js-cookie";
import { startCase, toLower } from "lodash";
import posthog from "posthog-js";
import { validate as uuidValidate } from "uuid";

import { ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED } from "~/shared/sharedConstants";

const COOKIE_VISIT_PRODUCT = "product";
const COOKIE_VISIT_ORDER_DETAILS_DATA = "orderDetailsData";
const COOKIE_USER_ID = "userID";
const COOKIE_GOKICK_UUID = "gokick_uuid"; // this is a anonymous UUID generated before the user signs up
const COOKIE_VISIT_ID = "visitID";
const COOKIE_VISIT_CONSULT_ANSWERS = "consultAnswers";
const COOKIE_VISIT_API_TOKEN = "apiTokenJWT";
const COOKIE_VISIT_API_TOKEN_RESTRICTED = "apiTokenRestricted";
const COOKIE_VISIT_DIFFERENT_PHARMACY = "differentPharmacy";
const COOKIE_VISIT_EMAIL = "email";
const COOKIE_VISIT_PHONE = "phone";
const COOKIE_VISIT_GENDER = "gender";
const COOKIE_VISIT_USSTATE = "usState";
const COOKIE_VISIT_BIRTHDAY = "birthday";
const COOKIE_VISIT_FIRSTNAME = "firstName";
const COOKIE_VISIT_LASTNAME = "lastName";
const COOKIE_VISIT_LATEST_INVOICE_ID = "latestInvoiceID";
const COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS = "latestInvoicePaymentStatus";
const COOKIE_VISIT_FOLLOW_UP_FLAG = "visitTypeFollowUp";
const COOKIE_LATEST_ADDRESS = "latestAddress";
const COOKIE_STRIPE_LINK_PERSISTENT_TOKEN = "stripe.link.persistent_token";
const COOKIE_VISIT_SHIPMENT_METHOD = "shipmentMethod";
const COOKIE_REWARDFUL_AFFILIATE_ID = "rfaid";
const COOKIE_IS_PICKUP = "isPickup";
const COOKIE_BB_VARIANT = "betaBlockerVariant";
const COOKIE_ORDER_COMPLETE = "orderComplete";
const COOKIE_SMS_CONSENT = "smsConsent";
const COOKIE_ORDER_ITEMS = "orderItems";
const COOKIE_ORIGINAL_REFILL_ORDER_ITEMS = "originalRefillOrderItems";
const COOKIE_USER_ALREADY_EXISTS = "userAlreadyExists";
const COOKIE_UPGRADING_TO_2_PACK = "upgradingTo2Pack";
const COOKIE_POST_CHECKOUT_INFO = "postCheckoutInfo";
const COOKIE_ACTIVE_SUBSCRIPTIONS = "activeSubscriptions";
const COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY =
  "isEditingPendingVisitPharmacy";
const COOKIE_CHECKOUT_SESSIONS = "checkoutSessions";

// expire in 2 weeks
const COOKIE_EXPIRES_DEFAULT = 14;

export function setVisitUserCookies(data) {
  setUserID(data.id);
  setVisitAPIToken(data.attributes.apiTokenJWT);
  setVisitAPITokenRestricted(data.attributes.restrictedToken);
  setVisitAPITokenLocalStorage(data.attributes.apiTokenJWT);
  setVisitEmail(toLower(data.attributes.email).trim());
  setVisitUSState(data.attributes.usState);
  setRewardfulAffiliateID(data.attributes.affiliateID);
  setSMSConsent(data.attributes.smsConsent);
  setShortIDMods(data);

  clearUserAlreadyExists();
}

function setShortIDMods(data) {
  for (let i = 2; i <= 7; i++) {
    setCookieWithExpiration(
      `shortIDMod${i}`,
      data.attributes[`shortIDMod${i}`],
      365
    );
  }
}

export function getCheckoutSession(checkoutSessionID) {
  const checkoutSessionsJSON =
    localStorage.getItem(COOKIE_CHECKOUT_SESSIONS) || "{}";
  const checkoutSessions = JSON.parse(checkoutSessionsJSON);
  return (
    checkoutSessions[checkoutSessionID] || { createdAt: new Date().getTime() }
  );
}

export function setCheckoutSession(checkoutSessionID, checkoutSession) {
  if (!uuidValidate(checkoutSessionID)) {
    console.trace(
      "setCheckoutSession called with invalid checkoutSessionID",
      checkoutSessionID
    );
  }
  const checkoutSessionsJSON =
    localStorage.getItem(COOKIE_CHECKOUT_SESSIONS) || "{}";
  const checkoutSessions = JSON.parse(checkoutSessionsJSON);
  checkoutSessions[checkoutSessionID] = checkoutSession;
  // keep only 10 sessions
  if (Object.keys(checkoutSessions).length > 10) {
    const oldestSessionID = Object.keys(checkoutSessions).sort(
      (a, b) => checkoutSessions[a].createdAt - checkoutSessions[b].createdAt
    )[0];
    delete checkoutSessions[oldestSessionID];
  }
  localStorage.setItem(
    COOKIE_CHECKOUT_SESSIONS,
    JSON.stringify(checkoutSessions)
  );
}

// Product
export function setVisitProduct(product, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setVisitProduct called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setVisitProduct");
  }
  clearOrderItems(checkoutSessionID);
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.visitProduct = product;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getVisitProduct(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getVisitProduct called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getVisitProduct");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.visitProduct;
}
export function clearVisitProduct(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearVisitProduct called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearVisitProduct");
  }
  clearOrderItems(checkoutSessionID);
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.visitProduct;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
// Order Address
export function setOrderDetailsData(orderDetailsData, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setOrderDetailsData called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setOrderDetailsData");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.orderDetailsData = orderDetailsData;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getOrderDetailsData(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getOrderDetailsData called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getOrderDetailsData");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  if (checkoutSession.orderDetailsData) {
    return typeof checkoutSession.orderDetailsData === "string"
      ? JSON.parse(decodeURIComponent(checkoutSession.orderDetailsData))
      : checkoutSession.orderDetailsData;
  }
  return {};
}
export function clearOrderDetailsData(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearOrderDetailsData called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearOrderDetailsData");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.orderDetailsData;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
// Visit ID
export function setVisitID(visitID, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setVisitID called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setVisitID");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.visitID = visitID;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getVisitID(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getVisitID called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getVisitID");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.visitID;
}
export function clearVisitID(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearVisitID called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearVisitID");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.visitID;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
// User ID
export function setUserID(userID) {
  return setCookieWithExpiration(COOKIE_USER_ID, userID);
}
export function getUserID() {
  return Cookies.get(COOKIE_USER_ID);
}
// gokick_uuid
export function getGokickUUID() {
  return Cookies.get(COOKIE_GOKICK_UUID);
}
// Short ID Mods
export function getUserShortIDMod(num) {
  return Cookies.get(`shortIDMod${num}`);
}
// Latest Address
export function setLatestAddress(address) {
  return setCookieWithExpiration(
    COOKIE_LATEST_ADDRESS,
    JSON.stringify(address)
  );
}
export function getLatestAddress() {
  if (Cookies.get(COOKIE_LATEST_ADDRESS)) {
    return JSON.parse(Cookies.get(COOKIE_LATEST_ADDRESS));
  }

  return {};
}

// Consult Answers
export function setConsultAnswersJSON(consultAnswers) {
  if (consultAnswers) {
    return localStorage.setItem(
      COOKIE_VISIT_CONSULT_ANSWERS,
      JSON.stringify(consultAnswers).replace(
        /<([^>]+?)([^>]*?)>(.*?)<\/\1>/gi, // remove html tags and inner content
        ""
      )
    );
  }
}

export function getConsultAnswersJSON() {
  return JSON.parse(localStorage.getItem(COOKIE_VISIT_CONSULT_ANSWERS));
}
export function clearConsultAnswersJSON() {
  console.log("clearConsultAnswersJSON");
  localStorage.removeItem(COOKIE_VISIT_CONSULT_ANSWERS);
}

// API Token
export function setVisitAPIToken(visitAPIToken) {
  return setCookieWithExpiration(COOKIE_VISIT_API_TOKEN, visitAPIToken);
}
export function getVisitAPIToken() {
  // Weird caching bug? where using Cookies.get(COOKIE_VISIT_API_TOKEN ) incorrectly returns a cleared value
  // return Cookies.get(COOKIE_VISIT_API_TOKEN);
  return Cookies.get()[COOKIE_VISIT_API_TOKEN];
}
export function setVisitAPITokenLocalStorage(visitAPIToken) {
  return localStorage.setItem(COOKIE_VISIT_API_TOKEN, visitAPIToken);
}
export function getVisitAPITokenLocalStorage() {
  return localStorage.getItem(COOKIE_VISIT_API_TOKEN);
}
// API Token Restricted
export function setVisitAPITokenRestricted(visitAPITokenRestricted) {
  return setCookieWithExpiration(
    COOKIE_VISIT_API_TOKEN_RESTRICTED,
    visitAPITokenRestricted
  );
}
export function getVisitAPITokenRestricted() {
  // Weird caching bug? where using Cookies.get(COOKIE_VISIT_API_TOKEN ) incorrectly returns a cleared value
  // return Cookies.get(COOKIE_VISIT_API_TOKEN);
  return Cookies.get()[COOKIE_VISIT_API_TOKEN_RESTRICTED];
}
// Different Pharmacy
export function setVisitDifferentPharmacyJSON(
  differentPharmacy,
  checkoutSessionID
) {
  if (!checkoutSessionID) {
    console.error(
      "setVisitDifferentPharmacyJSON called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for setVisitDifferentPharmacyJSON"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.differentPharmacy = differentPharmacy;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getVisitDifferentPharmacyJSON(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "getVisitDifferentPharmacyJSON called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for getVisitDifferentPharmacyJSON"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.differentPharmacy || null;
}
export function getVisitDifferentPharmacy(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getVisitDifferentPharmacy called without checkoutSessionID");
    throw new Error(
      "checkoutSessionID is required for getVisitDifferentPharmacy"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.differentPharmacy
    ? JSON.stringify(checkoutSession.differentPharmacy)
    : null;
}
export function clearVisitDifferentPharmacy(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "clearVisitDifferentPharmacy called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for clearVisitDifferentPharmacy"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.differentPharmacy;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
// Email
export function setVisitEmail(email) {
  return setCookieWithExpiration(COOKIE_VISIT_EMAIL, email);
}
export function getVisitEmail() {
  return Cookies.get(COOKIE_VISIT_EMAIL) || "";
}
// Gender
export function setVisitGender(gender) {
  return setCookieWithExpiration(COOKIE_VISIT_GENDER, gender);
}
export function getVisitGender() {
  return Cookies.get(COOKIE_VISIT_GENDER);
}
// Birthday
export function setVisitBirthday(birthday) {
  return setCookieWithExpiration(COOKIE_VISIT_BIRTHDAY, birthday);
}
export function getVisitBirthday() {
  return Cookies.get(COOKIE_VISIT_BIRTHDAY);
}
// Name
export function setVisitFirstName(firstName) {
  return setCookieWithExpiration(COOKIE_VISIT_FIRSTNAME, firstName);
}
export function getVisitFirstName() {
  return Cookies.get(COOKIE_VISIT_FIRSTNAME) || "";
}
export function setVisitLastName(lastName) {
  return setCookieWithExpiration(COOKIE_VISIT_LASTNAME, lastName);
}
export function getVisitLastName() {
  return Cookies.get(COOKIE_VISIT_LASTNAME) || "";
}
export function getVisitFullName() {
  return startCase(
    toLower(`${getVisitFirstName()} ${getVisitLastName()}`).trim()
  );
}
// Visit US State
export function setVisitUSState(usState) {
  if (usState) {
    return setCookieWithExpiration(COOKIE_VISIT_USSTATE, usState);
  }
}
export function getVisitUSState() {
  return Cookies.get(COOKIE_VISIT_USSTATE);
}
// Visit Phone
export function setVisitPhone(phone) {
  if (phone) {
    return setCookieWithExpiration(COOKIE_VISIT_PHONE, phone);
  }
}
export function getVisitPhone() {
  return Cookies.get(COOKIE_VISIT_PHONE) || "";
}
// Shipment Method
export function setShipmentMethod(shipmentMethod, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setShipmentMethod called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setShipmentMethod");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.shipmentMethod = shipmentMethod;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getShipmentMethod(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getShipmentMethod called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getShipmentMethod");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.shipmentMethod;
}
export function clearShipmentMethod(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearShipmentMethod called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearShipmentMethod");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.shipmentMethod;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
// Beta Blocker Variant
export function setBetaBlockerVariant(betaBlockerVariant, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setBetaBlockerVariant called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setBetaBlockerVariant");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.betaBlockerVariant = betaBlockerVariant;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getBetaBlockerVariant(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getBetaBlockerVariant called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getBetaBlockerVariant");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.betaBlockerVariant;
}
export function clearBetaBlockerVariant(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearBetaBlockerVariant called without checkoutSessionID");
    throw new Error(
      "checkoutSessionID is required for clearBetaBlockerVariant"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.betaBlockerVariant;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

// Latest Visit Invoice ID
export function setLatestVisitInvoiceID(invoiceID) {
  return setCookieWithExpiration(COOKIE_VISIT_LATEST_INVOICE_ID, invoiceID);
}
export function getLatestVisitInvoiceID() {
  return Cookies.get(COOKIE_VISIT_LATEST_INVOICE_ID) || "";
}
// Follow Up Visit Flag
export function setFollowUpVisitFlag(isFollowUpVisit, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setFollowUpVisitFlag called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setFollowUpVisitFlag");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.isFollowUpVisit = isFollowUpVisit;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function isFollowUpVisit(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("isFollowUpVisit called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for isFollowUpVisit");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.isFollowUpVisit == "yes";
}
export function clearFollowUpVisitFlag(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearFollowUpVisitFlag called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearFollowUpVisitFlag");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.isFollowUpVisit;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

// SMS Consent User (set from User record)
export function setSMSConsent(smsConsent) {
  return setCookieWithExpiration(COOKIE_SMS_CONSENT, smsConsent);
}
export function getSMSConsent() {
  return Cookies.get(COOKIE_SMS_CONSENT) === "true";
}

// Rewardful Affiliate ID
export function setRewardfulAffiliateID(affiliateID) {
  return setSessionCookie(COOKIE_REWARDFUL_AFFILIATE_ID, affiliateID);
}
export function getRewardfulAffiliateID() {
  return Cookies.get(COOKIE_REWARDFUL_AFFILIATE_ID);
}
// Stripe Link Persistent Token
export function setLinkPersistentTokenCookie(persistentToken) {
  Cookies.set(COOKIE_STRIPE_LINK_PERSISTENT_TOKEN, persistentToken, {
    expires: 365,
  });
}
export function getLinkPersistentTokenCookie() {
  return Cookies.get(COOKIE_STRIPE_LINK_PERSISTENT_TOKEN);
}
// Is Pickup (set in signup page modal)
export function setIsPickup(isPickup) {
  return setCookieWithExpiration(COOKIE_IS_PICKUP, isPickup || "");
}
export function getIsPickup() {
  return Cookies.get(COOKIE_IS_PICKUP);
}
// Order is complete
export function setOrderComplete(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setOrderComplete called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setOrderComplete");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.orderComplete = true;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getOrderComplete(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getOrderComplete called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getOrderComplete");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.orderComplete;
}
export function clearOrderComplete(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearOrderComplete called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearOrderComplete");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.orderComplete;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
function validateOrderItems(orderItems) {
  /*
    Rule 1:
      cant have a "prop-1-pack-flavored" with a "prop-trial-pack-flavored" addon
      if people get themselves into this state via multiple tab navigation, we set the
      "prop-1-pack-flavored" product to prop-1-pack (same with prop-2-pack-flavored and prop-2-pack)
      and clear the subcategory
  */
  for (let consultationType in orderItems) {
    if (orderItems[consultationType].productSlug === "prop-1-pack-flavored") {
      if (orderItems[consultationType].addons["prop-trial-pack-flavored"]) {
        orderItems[consultationType].productSlug = "prop-1-pack";
        delete orderItems[consultationType].subcategory;
      }
    }
  }
}
function setOrderItems(orderItems, checkoutSessionID) {
  validateOrderItems(orderItems);
  if (!checkoutSessionID) {
    console.error("setOrderItems called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setOrderItems");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.orderItems = orderItems;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getOrderItems(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getOrderItems called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getOrderItems");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.orderItems || {};
}
export function getOrderItemsList(checkoutSessionID) {
  const orderItemsByConsultationType = getOrderItems(checkoutSessionID);
  const orderItemsList = [];
  for (let key in orderItemsByConsultationType) {
    orderItemsList.push({
      consultationType: key,
      ...orderItemsByConsultationType[key],
    });
    if (orderItemsByConsultationType[key].addons) {
      Object.keys(orderItemsByConsultationType[key].addons).forEach(
        (addonProductSlug) => {
          orderItemsList.push({
            consultationType: key,
            productSlug: addonProductSlug,
            isAddon: true,
            ...orderItemsByConsultationType[key].addons[addonProductSlug],
          });
        }
      );
    }
  }
  return orderItemsList;
}
export function getOrderItemAddonsList(checkoutSessionID) {
  const orderItemsList = getOrderItemsList(checkoutSessionID);
  return orderItemsList.filter(
    ({ productSlug }) =>
      productSlug && productSlug !== getVisitProduct(checkoutSessionID)
  );
}

export function setConsultationTypeOrderItem(
  consultationType,
  item,
  checkoutSessionID
) {
  /* Order items cookie looks like this: (1 product per consultation type for now but that 1 product can have addons)
    {
      pac: {
        productSlug: "prop-1-pack",
        quantity: 1,
        addons: {
          "prop-trial-pack-flavored": {
            subcategory: "tangerine",
          }
        }
      },
      sca: {
        productSlug: "sco-addon",
        quantity: 1,
      },
    }
  */
  const orderItems = getOrderItems(checkoutSessionID);
  const existingSubcategory = orderItems[consultationType]?.subcategory;
  orderItems[consultationType] = {
    productSlug: item,
    quantity: 1,
    addons: {},
    ...(existingSubcategory && { subcategory: existingSubcategory }),
  };
  setOrderItems(orderItems, checkoutSessionID);
}
export function setConsultationTypeOrderItemAddon(
  consultationType,
  item,
  subcategory,
  checkoutSessionID
) {
  /* Order items cookie looks like this: (1 product per consultation type for now)
    {
      pac: {
        productSlug: "prop-1-pack",
        quantity: 1,
      },
      sca: {
        productSlug: "sco-addon",
        quantity: 1,
      },
    }
  */
  const orderItems = getOrderItems(checkoutSessionID);
  orderItems[consultationType] = {
    ...orderItems[consultationType],
    addons: {
      ...orderItems[consultationType].addons,
      [item]: {
        subcategory,
        quantity: 1,
      },
    },
  };
  setOrderItems(orderItems, checkoutSessionID);
}

export function removeConsultationTypeOrderItemAddon(
  consultationType,
  item,
  checkoutSessionID
) {
  const orderItems = getOrderItems(checkoutSessionID);
  if (orderItems?.[consultationType]) {
    delete orderItems[consultationType].addons?.[item];
    setOrderItems(orderItems, checkoutSessionID);
  }
}

export function clearOrderItems(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearOrderItems called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearOrderItems");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.orderItems;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function clearUserCookies() {
  Cookies.remove(COOKIE_SMS_CONSENT);
  Cookies.remove(COOKIE_LATEST_ADDRESS);
  Cookies.remove(COOKIE_VISIT_PHONE);
  Cookies.remove(COOKIE_VISIT_GENDER);
  Cookies.remove(COOKIE_USER_ID);
}

export function clearVisitCookies() {
  Cookies.remove(COOKIE_BB_VARIANT);
  Cookies.remove(COOKIE_IS_PICKUP);
  Cookies.remove(COOKIE_ORDER_COMPLETE);
  Cookies.remove(COOKIE_ORDER_ITEMS);
  Cookies.remove(COOKIE_REWARDFUL_AFFILIATE_ID);
  Cookies.remove(COOKIE_UPGRADING_TO_2_PACK);
  Cookies.remove(COOKIE_VISIT_CONSULT_ANSWERS);
  Cookies.remove(COOKIE_VISIT_FOLLOW_UP_FLAG);
  Cookies.remove(COOKIE_VISIT_ID);
  Cookies.remove(COOKIE_VISIT_LATEST_INVOICE_ID);
  Cookies.remove(COOKIE_VISIT_LATEST_INVOICE_PAYMENT_STATUS);
  Cookies.remove(COOKIE_VISIT_ORDER_DETAILS_DATA);
  Cookies.remove(COOKIE_VISIT_PRODUCT);
  Cookies.remove(COOKIE_VISIT_SHIPMENT_METHOD);
  Cookies.remove(COOKIE_IS_EDITING_PENDING_VISIT_PHARMACY);

  localStorage.removeItem(COOKIE_VISIT_DIFFERENT_PHARMACY);
  localStorage.removeItem(COOKIE_VISIT_CONSULT_ANSWERS);
  localStorage.removeItem(COOKIE_POST_CHECKOUT_INFO);
  localStorage.removeItem(COOKIE_ACTIVE_SUBSCRIPTIONS);
  localStorage.removeItem(COOKIE_CHECKOUT_SESSIONS);
}

export function clearUserAndVisitCookies() {
  clearUserCookies();
  clearVisitCookies();
}

export function clearAuthCookies() {
  console.log("clearAuthCookies");
  Cookies.remove(COOKIE_VISIT_API_TOKEN);
  localStorage.removeItem(COOKIE_VISIT_API_TOKEN);
}

export function logVisitUserOut() {
  clearVisitCookies();
  clearUserCookies();
  clearAuthCookies();
  posthog.reset();
}

export function isLoggedIn() {
  // TODO: check expiration of JWT Token
  const token = getVisitAPIToken();
  return token != null && token != "";
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
export function storageAvailable(type) {
  var storage;
  try {
    storage = window[type];
    var x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === "QuotaExceededError" ||
        // Firefox
        e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
export function isLocalStorageAvailable() {
  return storageAvailable("localStorage");
}

export function isSessionStorageAvailable() {
  return storageAvailable("sessionStorage");
}

export function areCookiesAvailable() {
  try {
    const testKey = "storageTest";
    const testValue = "works!";

    Cookies.set(testKey, testValue);
    if (Cookies.get(testKey) != testValue) {
      return false;
    }
  } catch (error) {
    return false;
  }
  return true;
}

// persistent cookie with expiration
function setCookieWithExpiration(key, value, expires = null) {
  if (expires == null) {
    return Cookies.set(key, value, { expires: COOKIE_EXPIRES_DEFAULT });
  } else {
    return Cookies.set(key, value, { expires: expires });
  }
}

function setSessionCookie(key, value) {
  // omitting "expires" sets a session-only cookie
  return Cookies.set(key, value);
}

export function setUserAlreadyExists(email) {
  return setCookieWithExpiration(COOKIE_USER_ALREADY_EXISTS, email || "");
}

export function getUserAlreadyExists() {
  return Cookies.get(COOKIE_USER_ALREADY_EXISTS, "");
}

export function clearUserAlreadyExists() {
  return Cookies.remove(COOKIE_USER_ALREADY_EXISTS);
}

export function setUpgradingTo2Pack(value, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setUpgradingTo2Pack called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setUpgradingTo2Pack");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.upgradingTo2Pack = value;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getUpgradingTo2Pack(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getUpgradingTo2Pack called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getUpgradingTo2Pack");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.upgradingTo2Pack;
}
export function clearUpgradingTo2Pack(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearUpgradingTo2Pack called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearUpgradingTo2Pack");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.upgradingTo2Pack;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function getForceFlavoredBBs(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getForceFlavoredBBs called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getForceFlavoredBBs");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.forceFlavoredBBs;
}

export function setForceFlavoredBBs(forceFlavoredBBs, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setForceFlavoredBBs called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setForceFlavoredBBs");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.forceFlavoredBBs = forceFlavoredBBs;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function clearForceFlavoredBBs(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearForceFlavoredBBs called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearForceFlavoredBBs");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.forceFlavoredBBs;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function setActiveSubscriptionBySubType(subType, sub) {
  localStorage.setItem(
    COOKIE_ACTIVE_SUBSCRIPTIONS,
    JSON.stringify({
      ...JSON.parse(localStorage.getItem(COOKIE_ACTIVE_SUBSCRIPTIONS)),
      [subType]: sub,
    })
  );
}
export function getActiveSubscriptionBySubType(subType) {
  const activeSub = JSON.parse(
    localStorage.getItem(COOKIE_ACTIVE_SUBSCRIPTIONS) || "{}"
  );
  return activeSub[subType];
}
export function clearActiveSubscriptionsBySubType() {
  localStorage.removeItem(COOKIE_ACTIVE_SUBSCRIPTIONS);
}

export function setPostCheckoutInfo(postCheckoutInfo, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("setPostCheckoutInfo called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for setPostCheckoutInfo");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.postCheckoutInfo = postCheckoutInfo;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}
export function getPostCheckoutInfo(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("getPostCheckoutInfo called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for getPostCheckoutInfo");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.postCheckoutInfo || {};
}
export function clearPostCheckoutInfo(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error("clearPostCheckoutInfo called without checkoutSessionID");
    throw new Error("checkoutSessionID is required for clearPostCheckoutInfo");
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.postCheckoutInfo;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function setOrderItemSubcategory(
  consultationType,
  subcategory,
  checkoutSessionID
) {
  const orderItems = getOrderItems(checkoutSessionID);
  orderItems[consultationType].subcategory = subcategory;
  setOrderItems(orderItems, checkoutSessionID);
}

export function getOrderItemSubcategory(consultationType, checkoutSessionID) {
  const orderItems = getOrderItems(checkoutSessionID);
  return orderItems[consultationType]?.subcategory;
}

export function clearOrderItemSubcategory(consultationType, checkoutSessionID) {
  const orderItems = getOrderItems(checkoutSessionID);
  delete orderItems[consultationType].subcategory;
  setOrderItems(orderItems, checkoutSessionID);
}

export function getOrderItemSubcategoryFromProductSlug(
  productSlug,
  checkoutSessionID
) {
  const orderItems = getOrderItems(checkoutSessionID);
  for (const consultationType in orderItems) {
    if (orderItems[consultationType].productSlug === productSlug) {
      return orderItems[consultationType].subcategory;
    }
  }
  return null;
}

export function setOriginalRefillOrderItems(orderItems, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "setOriginalRefillOrderItems called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for setOriginalRefillOrderItems"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.originalRefillOrderItems = orderItems;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function getOriginalRefillOrderItems(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "getOriginalRefillOrderItems called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for getOriginalRefillOrderItems"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.originalRefillOrderItems || [];
}

export function clearOriginalRefillOrderItems(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "clearOriginalRefillOrderItems called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for clearOriginalRefillOrderItems"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.originalRefillOrderItems;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

const mailOrderOnlyAddons = [ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED];
export function clearMailOrderOnlyAddonsForConsultationType(
  consultationType,
  checkoutSessionID
) {
  mailOrderOnlyAddons.forEach((addon) => {
    removeConsultationTypeOrderItemAddon(
      consultationType,
      addon,
      checkoutSessionID
    );
  });
}

export function setIsEditingPendingVisitPharmacy(isEditing, checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "setIsEditingPendingVisitPharmacy called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for setIsEditingPendingVisitPharmacy"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  checkoutSession.isEditingPendingVisitPharmacy = isEditing;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

export function getIsEditingPendingVisitPharmacy(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "getIsEditingPendingVisitPharmacy called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for getIsEditingPendingVisitPharmacy"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  return checkoutSession.isEditingPendingVisitPharmacy;
}

export function clearIsEditingPendingVisitPharmacy(checkoutSessionID) {
  if (!checkoutSessionID) {
    console.error(
      "clearIsEditingPendingVisitPharmacy called without checkoutSessionID"
    );
    throw new Error(
      "checkoutSessionID is required for clearIsEditingPendingVisitPharmacy"
    );
  }
  const checkoutSession = getCheckoutSession(checkoutSessionID) || {};
  delete checkoutSession.isEditingPendingVisitPharmacy;
  setCheckoutSession(checkoutSessionID, checkoutSession);
}

// Promo Code functions
export function getPromoCode(checkoutSessionID) {
  const key = `promoCode_${checkoutSessionID}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : null;
}

export function setPromoCode(promoCodeData, checkoutSessionID) {
  const key = `promoCode_${checkoutSessionID}`;
  localStorage.setItem(key, JSON.stringify(promoCodeData));
}

export function clearPromoCode(checkoutSessionID) {
  const key = `promoCode_${checkoutSessionID}`;
  localStorage.removeItem(key);
}
